import { ReactNode, useEffect } from 'react'
import { skipLinkStyles, srOnlyStyles, focusVisibleStyles, prefersReducedMotion } from '../../utils/accessibility'

interface AccessibleLayoutProps {
  children: ReactNode
}

/**
 * AccessibleLayout wraps the app with accessibility features:
 * - Skip links for keyboard navigation
 * - Screen reader only utility classes
 * - Focus visible styles
 * - Reduced motion support
 */
export function AccessibleLayout({ children }: AccessibleLayoutProps) {
  useEffect(() => {
    // Add reduced motion class if user prefers
    if (prefersReducedMotion()) {
      document.documentElement.classList.add('reduce-motion')
    }

    // Set lang attribute for screen readers
    document.documentElement.setAttribute('lang', 'pt-BR')

    return () => {
      document.documentElement.classList.remove('reduce-motion')
    }
  }, [])

  return (
    <>
      {/* Skip Links */}
      <a href="#main-content" className="skip-link">
        Ir para o conteúdo principal
      </a>
      <a href="#main-navigation" className="skip-link" style={{ left: '200px' }}>
        Ir para navegação
      </a>

      {/* Main content */}
      {children}

      {/* Global accessibility styles */}
      <style>{`
        ${skipLinkStyles}
        ${srOnlyStyles}
        ${focusVisibleStyles}

        /* Reduced motion support */
        .reduce-motion *,
        .reduce-motion *::before,
        .reduce-motion *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }

        /* Focus styles for interactive elements */
        .interactive-element:focus-visible {
          outline: 2px solid #6366f1;
          outline-offset: 2px;
          border-radius: 4px;
        }

        /* Button focus ring */
        button:focus-visible {
          outline: 2px solid #6366f1;
          outline-offset: 2px;
        }

        /* Link focus styles */
        a:focus-visible {
          outline: 2px solid #6366f1;
          outline-offset: 2px;
          border-radius: 2px;
        }

        /* Input focus styles */
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible {
          outline: none;
          ring: 2px solid #6366f1;
          border-color: #6366f1;
        }

        /* High contrast mode adjustments */
        @media (prefers-contrast: more) {
          * {
            border-color: currentColor !important;
          }

          button, a {
            border: 2px solid currentColor !important;
          }
        }

        /* Touch target size (minimum 44x44px for WCAG) */
        @media (pointer: coarse) {
          button,
          [role="button"],
          a {
            min-height: 44px;
            min-width: 44px;
          }
        }
      `}</style>
    </>
  )
}
